import { useTruncationDetection } from '@anchorage/hooks';
import { Icon } from '@latitude/icons';
import { cn } from '@latitude/token-utils';
import { Tooltip } from '@latitude/tooltip';
import { Slot } from '@radix-ui/react-slot';
import type { Ref } from 'react';
import type { TagProps } from 'types';
import {
  iconVariants,
  leadingIconVariants,
  tagVariants,
  textAreaVariants,
} from 'variants/variants';

export type { TagVariant } from 'types';

type TagBaseProps = Omit<TagProps, 'truncate'> & {
  ref?: Ref<HTMLDivElement>;
  textRef?: Ref<HTMLSpanElement>;
  truncate: boolean;
};

const TagBase = ({
  className,
  variant = 'primary',
  size = 'small',
  fill,
  asChild = false,
  children,
  leadingIcon,
  selected = false,
  disabled = false,
  onRemove,
  onClick,
  spanProps = {},
  rawProps = {},
  'data-testid': testID,
  readOnly = false,
  truncate,
  ref,
  textRef,
}: TagBaseProps) => {
  const Comp = asChild ? Slot : 'div';
  const isClickable = !readOnly && !!onClick;
  return (
    <Comp
      aria-selected={selected}
      aria-disabled={disabled}
      data-clickable={isClickable}
      ref={ref}
      data-testid={testID}
      {...rawProps}
      className={cn(
        tagVariants({
          variant,
          fill,
          size,
          clickable: isClickable,
          truncate,
        }),
        className,
        rawProps.className,
      )}
      onClick={readOnly ? undefined : onClick}
    >
      <div className={'relative flex w-full items-center'}>
        {leadingIcon && (
          <span
            className={leadingIconVariants({ size })}
            data-testid="tag-leading-icon"
          >
            {leadingIcon}
          </span>
        )}
        <span
          {...spanProps}
          ref={textRef}
          className={cn(
            textAreaVariants({
              size,
              withLeadingIcon: !!leadingIcon,
              truncate,
            }),
            spanProps.className,
          )}
        >
          {children}
        </span>
        {onRemove && !readOnly && (
          <Icon
            name="x-close"
            className={cn(iconVariants({ size }))}
            onClick={onRemove}
            data-testid="remove-icon"
          />
        )}
      </div>
    </Comp>
  );
};

// Split out so useTruncationDetection only mounts for tags that opt into truncate.
// Tooltip wraps its trigger in a flex-column div; without these wrapper classes a
// long tag overflows a flex row and a short one stretches to fill a table cell.
const TruncatedTag = (props: Omit<TagBaseProps, 'truncate' | 'textRef'>) => {
  const { ref: textRef, isTruncated } = useTruncationDetection();

  return (
    <Tooltip
      text={props.children}
      clampLines={false}
      wrapperClassName="min-w-0 max-w-full items-start"
      hideTooltip={!isTruncated}
    >
      <TagBase {...props} truncate textRef={textRef} />
    </Tooltip>
  );
};

const Tag = ({
  truncate = false,
  ...props
}: TagProps & {
  ref?: Ref<HTMLDivElement>;
}) =>
  truncate ? (
    <TruncatedTag {...props} />
  ) : (
    <TagBase {...props} truncate={false} />
  );

Tag.displayName = 'Tag';
export { Tag };
